import type { Note } from '../song';
import { transpose } from '../score';
import { DREAM_PLAN, KEYS, MAJOR, flavored } from './dream';
import type { Bar, Chord, Ctx } from './dream';

const CHORDS: Readonly<Record<string, Chord>> = {
  Am9: { root: 'A1', pad: ['C3', 'G3', 'B3', 'E4'] },
  Fmaj9: { root: 'F1', pad: ['C3', 'G3', 'A3', 'E4'] },
  Cmaj7: { root: 'C2', pad: ['E3', 'G3', 'B3', 'D4'] },
  G6: { root: 'G1', pad: ['D3', 'E3', 'B3', 'G4'] },
  Em11: { root: 'E2', pad: ['D3', 'A3', 'B3', 'G4'] },
  Dm9: { root: 'D2', pad: ['F3', 'C4', 'E4', 'A4'] },
};

const HOOK: Note[][] = [
  [[0, 'E5', 2], [2, 'C5', 4], [8, 'B4', 2], [10, 'E5', 6]],
  [[0, 'A5', 2], [2, 'G5', 4], [8, 'E5', 2], [10, 'C5', 6]],
  [[0, 'G5', 2], [2, 'E5', 4], [8, 'B5', 2], [10, 'D6', 6]],
  [[0, 'D6', 2], [2, 'B5', 4], [8, 'G5', 2], [10, 'E5', 4], [14, 'D5', 2]],
];

const COUNT: Readonly<Record<string, number>> = { intro: 2, A: 2, drop: 3, drop2: 3, break: 4, outro: 2 };

function extra(x: Ctx, b: Bar): void {
  if (b.end) return;
  const high = [...b.bells.map((n) => transpose(n, 12)), ...b.bells.map((n) => transpose(n, 24))];
  const n = COUNT[b.name] ?? 0;
  for (let k = 0; k < n; k++) x.play('spark', b.bar, x.rng.int(0, 16), x.rng.pick(high), x.s * 6, 0.25 + x.rng.float() * 0.25);
  if ((b.name === 'break' && b.index % 2 === 0) || (b.name === 'intro' && b.index === 0)) x.chord('shim', b.bar, 0, b.bells.map((n) => transpose(n, 12)), x.g.barLength * 2, 0.45, 0.05);
  if (b.i === 3 && (b.groove || b.drop)) x.push({ v: 'swell', t: x.g.at(b.bar, 8), end: x.g.at(b.bar + 1), bar: b.bar, step: 8, vel: 1 });
}

export const AURORA = flavored({
  id: 'aurora',
  genre: 'aurora chillwave / snow-globe ambient pop',
  bpm: [118, 126],
  ref: { seed: 'neve', title: 'Neve', params: { bpm: 120, bars: 28, energy: 0.55 } },
  chords: CHORDS,
  progs: [
    ['Am9', 'Fmaj9', 'Cmaj7', 'G6'],
    ['Am9', 'Em11', 'Fmaj9', 'G6'],
    ['Fmaj9', 'G6', 'Em11', 'Am9'],
    ['Dm9', 'Am9', 'Fmaj9', 'Em11'],
  ],
  hook: HOOK,
  arps: [
    [0, 2, 1, 3],
    [1, 3, 0, 2, 3, 1],
    [3, 1, 2, 0, 2, 1],
  ],
  keys: KEYS,
  scale: MAJOR,
  range: [69, 86],
  center: 76,
  plan: DREAM_PLAN,
  swing: 54,
  lufs: -12,
  mix: (bpm) => ({ reverbDecay: 6.5, delayTime: (60 / bpm) * 1.5, delayFeedback: 0.42, drive: 1.05, softness: 1.6, pumpDepth: 0.2, pumpRelease: 0.45 }),
  kick: { groove: 'X.........x.....', drop: 'X.......X.x.....' },
  back: { groove: '........x.......', drop: '....x.......x...', vel: [0.6, 0.8] },
  hats: ['-.x.-.x.-.x.-.x.', 'xoxoxoxoxoxoxoxo', 'x-xox-xox-xox-xo'],
  open: { pattern: '......x.......x.', from: 0.75 },
  bass: {
    groove: [
      [0, 0, 11.5, 0.85],
      [12, 0, 3.5, 0.7],
    ],
    drop: [
      [0, 0, 7.5, 0.9],
      [8, 0, 2, 0.8],
      [10, 0, 5.5, 0.8],
    ],
  },
  arp: { groove: 'Xx......Xx......', drop: 'Xx..Xx..Xx..Xx..', len: 3 },
  bells: (pad) => pad.map((n) => transpose(n, 12)),
  harm: 2,
  extra,
  accents: { kick: 'kick', snare: 'snare', pad: 'stab', lead: 'melody', impact: 'drop' },
});
